import { MoreHorizontal } from "lucide-react";
import { TableCell, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useState } from "react";
import { fmtDate } from "@/lib/api";
import { COLLECTION_LABELS } from "@/lib/sp";
import { KeyStatus } from "@/components/apikeys/keyUtils";

const CONFIRM = {
  regenerate: ["Régénérer la clé ?", "Une nouvelle clé sera créée et l'ancienne cessera immédiatement de fonctionner."],
  revoke: ["Révoquer définitivement ?", "La clé sera bloquée pour toujours. Cette action est irréversible."],
};

export const KeyRow = ({ k, onEdit, onRegenerate, onRevoke, onToggle }) => {
  const [confirm, setConfirm] = useState(null);
  const scopes = Object.entries(k.scopes || {});
  const dead = k.status === "revoked";
  return (
    <TableRow data-testid={`key-row-${k.id}`}>
      <TableCell><p className="font-medium text-stone-900">{k.name}</p><p className="text-xs text-stone-500 max-w-[220px] truncate">{k.description || `Créée par ${k.created_by}`}</p></TableCell>
      <TableCell><code className="text-xs bg-stone-100 rounded px-1.5 py-0.5" data-testid={`key-prefix-${k.id}`}>{k.prefix}…</code></TableCell>
      <TableCell className="text-xs text-stone-700 max-w-[240px]">
        {scopes.slice(0, 3).map(([n, l]) => <span key={n} className="inline-block mr-1 mb-1 rounded bg-stone-100 px-1.5 py-0.5">{COLLECTION_LABELS[n] || n}{l === "write" ? " ✎" : ""}</span>)}
        {scopes.length > 3 && <span className="text-stone-500">+{scopes.length - 3}</span>}
      </TableCell>
      <TableCell className="text-xs text-stone-600 whitespace-nowrap">
        <p>{k.rate_limit}/min</p><p>{k.allowed_ips?.length ? `${k.allowed_ips.length} IP` : "Toutes IP"}</p><p>{k.expires_at ? `Expire ${fmtDate(k.expires_at)}` : "Sans expiration"}</p>
      </TableCell>
      <TableCell className="text-xs text-stone-600 whitespace-nowrap"><p className="font-medium text-stone-900" data-testid={`key-calls-${k.id}`}>{k.calls_total || 0} appels</p><p>{k.last_used_at ? fmtDate(k.last_used_at.slice(0, 16)) : "Jamais utilisée"}</p></TableCell>
      <TableCell><KeyStatus status={k.status} testId={`key-status-${k.id}`} /></TableCell>
      <TableCell>
        {!dead && (
          <DropdownMenu>
            <DropdownMenuTrigger className="p-1.5 rounded hover:bg-stone-100" data-testid={`key-actions-${k.id}`}><MoreHorizontal className="h-4 w-4" /></DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onEdit} data-testid={`key-edit-${k.id}`}>Modifier</DropdownMenuItem>
              {k.status !== "expired" && <DropdownMenuItem onClick={onToggle} data-testid={`key-toggle-${k.id}`}>{k.status === "active" ? "Suspendre" : "Réactiver"}</DropdownMenuItem>}
              <DropdownMenuItem onClick={() => setConfirm("regenerate")} data-testid={`key-regenerate-${k.id}`}>Régénérer</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-red-700" onClick={() => setConfirm("revoke")} data-testid={`key-revoke-${k.id}`}>Révoquer</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
          <AlertDialogContent>
            <AlertDialogHeader><AlertDialogTitle>{confirm && CONFIRM[confirm][0]}</AlertDialogTitle><AlertDialogDescription>{confirm && CONFIRM[confirm][1]}</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="confirm-cancel">Annuler</AlertDialogCancel>
              <AlertDialogAction className="bg-[#C2410C] hover:bg-[#9A3412]" data-testid="confirm-ok" onClick={() => (confirm === "revoke" ? onRevoke() : onRegenerate())}>Confirmer</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </TableCell>
    </TableRow>
  );
};
